/**
 * Pure helpers that shape a personalized copy for the email, Slack and slide
 * views: brand colours that stay readable, bars scaled to the tallest value,
 * and the short labels the views print. No React, no fetching.
 */
import { darkThemeColors, lightThemeColors } from "@magicbi/ui-library";
import { rawHex } from "../design-system/tokens";
import type {
  DistributionApi,
  FormatApi,
  RecipientBrandApi,
} from "../services/distributions/distributions.types";
import type {
  BarView,
  CopyBrandView,
  HBarView,
  InkTone,
  SlideTone,
} from "./distributionCopyView.types";

/** WCAG AA for body text. */
export const MIN_TEXT_CONTRAST = 4.5;

/** The short tag on a file ("PDF", "PPT"). */
export const FORMAT_TAG_LABEL: Record<FormatApi, string> = {
  pdf: "PDF",
  pptx: "PPT",
};

/** The format's name in a sentence ("PDF", "PowerPoint"). */
export const FORMAT_NAME: Record<FormatApi, string> = {
  pdf: "PDF",
  pptx: "PowerPoint",
};

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

export const isHexColor = (value: string | null | undefined): value is string =>
  HEX_COLOR.test(value ?? "");

const linearChannel = (value: number): number => {
  const s = value / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance of a "#RRGGBB" colour, 0 (black) to 1 (white). */
export const relativeLuminance = (hex: string): number => {
  const n = parseInt(hex.slice(1), 16);
  return (
    0.2126 * linearChannel((n >> 16) & 255) +
    0.7152 * linearChannel((n >> 8) & 255) +
    0.0722 * linearChannel(n & 255)
  );
};

/** WCAG contrast ratio between two "#RRGGBB" colours, 1 to 21. */
export const contrastRatio = (a: string, b: string): number => {
  const x = relativeLuminance(a);
  const y = relativeLuminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
};

/**
 * White text on the accent when it reads at 4.5:1, ink otherwise. A light
 * accent such as a pale yellow switches its band text to ink.
 */
export const inkOnAccent = (accent: string): InkTone =>
  contrastRatio(accent, rawHex.text.white) >= MIN_TEXT_CONTRAST ? "light" : "dark";

/** The page colour of a slide in each tone; the slide styles use the same tokens. */
export const SLIDE_BACKGROUND: Record<SlideTone, string> = {
  light: lightThemeColors.background.primary,
  dark: darkThemeColors.background.primary,
};

/**
 * Slide titles take the brand accent only when it reads on the slide's
 * background; otherwise they stay in the tone's ink.
 */
export const slideTitleColor = (accent: string, tone: SlideTone): "accent" | "ink" =>
  isHexColor(accent) && contrastRatio(accent, SLIDE_BACKGROUND[tone]) >= MIN_TEXT_CONTRAST
    ? "accent"
    : "ink";

const toPct = (value: number, max: number): number =>
  max > 0 ? Math.round((Math.max(0, value) / max) * 1000) / 10 : 0;

/** Vertical bars scaled to the tallest value; the last bar is the latest point. */
export const toBarViews = (values: number[]): BarView[] => {
  const max = Math.max(0, ...values);
  return values.map((value, index) => ({
    key: `bar-${index}`,
    pct: toPct(value, max),
    highlight: index === values.length - 1,
  }));
};

/** Horizontal bar rows scaled to the largest value. */
export const toHBarViews = (
  rows: { label: string; value: number; formatted: string }[],
): HBarView[] => {
  const max = Math.max(0, ...rows.map((row) => row.value));
  return rows.map((row, index) => ({
    key: `hbar-${index}-${row.label}`,
    label: row.label,
    pct: toPct(row.value, max),
    formatted: row.formatted,
  }));
};

/** First letters of up to two words: "Central Co-op" → "CC". */
export const markFromName = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

/**
 * The distribution's default branding, named after the organisation when the owner left the name
 * blank, with its initials as the mark unless the owner set one. Never the sender's name: the
 * copies speak for the organisation.
 */
export const withOrganisationBrand = (
  branding: RecipientBrandApi,
  organisation: string | null,
): RecipientBrandApi => {
  const name = organisation?.trim();
  // A brand kit gives the name and mark (round 6); Story Manager falls back to the organisation.
  if (branding.kit_id || branding.name?.trim() || !name) return branding;
  return { ...branding, name, mark: branding.mark?.trim() || markFromName(name) };
};

/** A distribution whose default branding falls back to the organisation (`withOrganisationBrand`). */
export const withOrganisationBranding = (
  distribution: DistributionApi,
  organisation: string | null,
): DistributionApi => {
  if (!organisation?.trim()) return distribution;
  const branding = withOrganisationBrand(distribution.content.branding, organisation);
  return branding === distribution.content.branding
    ? distribution
    : { ...distribution, content: { ...distribution.content, branding } };
};

/**
 * A recipient's own branding over the distribution's defaults, field by field.
 * An invalid accent falls back to the default so a copy never draws in a
 * colour the owner didn't pick. A row that names its own brand never borrows
 * the default logo or mark, which belong to another organisation.
 */
export const toCopyBrandView = (
  own: RecipientBrandApi | null,
  defaults: RecipientBrandApi,
): CopyBrandView => {
  const ownName = own?.name?.trim() || "";
  const name = ownName || defaults.name?.trim() || "";
  const accent = isHexColor(own?.accent)
    ? own!.accent!
    : isHexColor(defaults.accent)
      ? defaults.accent!
      : rawHex.brand.primary;
  const defaultMark = defaults.mark?.trim() || markFromName(name);
  const mark = own?.mark?.trim() || (ownName ? markFromName(ownName) : defaultMark);
  return {
    name,
    mark: mark.slice(0, 2),
    accent: accent.toUpperCase(),
    ink: inkOnAccent(accent),
    logoUrl: own?.logo_url || (ownName ? null : defaults.logo_url) || null,
  };
};
